import { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctionSignature, extractFunctionBody, TAB } from '../components/utils';
import {
    deriveParserSelectionsFromCode,
    generateInitialParserCode,
} from '../components/parserCodeGenerator';

export const functionParserExample = '# Implement your logic here to return the expected output';

const normalizeCode = (code) => (code || '').replace(/\s+/g, '').replace(/["']/g, '"');

/**
 * Parser-function code state, extracted from ModelSetupPage so the Playground
 * Inspect modal can drive the same code lifecycle. Owns: the editable parser
 * body (`userCode`), auto-generated-snippet tracking, loading + deriving
 * selections from the usecase's saved `parser_function`, regenerating the code
 * from mapping prefill, the Tab-indent key handler, and the full function code
 * (signature + indented body) sent to `/validate-code`.
 *
 * `setResponseType` is called when loading/editing reveals the response type
 * (custom code -> 'custom', text parser -> 'text', otherwise 'json').
 */
export const useParserCodeState = ({ usecase, setResponseType }) => {
    const [userCode, setUserCodeState] = useState(functionParserExample);
    const [lastAutoGeneratedSnippet, setLastAutoGeneratedSnippet] = useState(null);
    const [parserPrefill, setParserPrefill] = useState({
        classification: null,
        ner: null,
        relations: null,
        summarization: null,
    });
    const [parserSelectionsFromCode, setParserSelectionsFromCode] = useState(null);

    const updateUserCode = useCallback((value, options = {}) => {
        const nextValue = value ?? '';
        setUserCodeState(nextValue);
        if (options.fromAuto) {
            setLastAutoGeneratedSnippet(nextValue.trim());
        } else {
            setLastAutoGeneratedSnippet(null);
            // If manual edit, and not default, switch to custom
            const isDefault = normalizeCode(nextValue) === normalizeCode(functionParserExample);
            if (!isDefault) {
                setResponseType('custom');
            }
        }
    }, [setResponseType]);

    // Update userCode when the usecase changes
    useEffect(() => {
        if (usecase && usecase.parser_function) {
            const parsedCode = extractFunctionBody(usecase.parser_function);

            if (parsedCode) {
                // Try to see if current code matches generated code for these selections
                // If so, mark it as auto-generated so future clicks update it
                const selections = deriveParserSelectionsFromCode(usecase.parser_function, usecase?.task_type, usecase?.is_multilabel);
                const prefillCandidate = {
                    classification: selections?.classification || null,
                    ner: selections?.ner || null,
                    relations: selections?.relations || null,
                    summarization: selections?.summarization || null,
                };
                const generated = generateInitialParserCode(usecase.task_type, prefillCandidate);

                const isMatch = generated && normalizeCode(generated) === normalizeCode(parsedCode);
                // Also check if it matches the default example (functionParserExample)
                const isDefault = normalizeCode(parsedCode) === normalizeCode(functionParserExample);

                updateUserCode(parsedCode, { fromAuto: isMatch || isDefault });

                // If it is NOT default and NOT auto-generated (match), set to custom
                // BUT: if it matches the auto-generated code, it means it fits the "JSON" mode derived logic.
                // So only set to 'custom' if it does NOT match generated logic AND is not default.
                if (!isDefault && !isMatch) {
                    setResponseType('custom');
                } else if (selections?.classification?.type === 'text') {
                    setResponseType('text');
                } else if (selections?.summarization?.type === 'text') {
                    setResponseType('text');
                } else {
                    setResponseType('json');
                }
            } else {
                updateUserCode(functionParserExample);
                setResponseType('json');
            }

            const selections = deriveParserSelectionsFromCode(usecase.parser_function, usecase?.task_type, usecase?.is_multilabel);
            setParserSelectionsFromCode(selections);
            if (selections?.classification) {
                setParserPrefill((prev) => ({ ...prev, classification: selections.classification }));
            }
            if (selections?.ner) {
                setParserPrefill((prev) => ({ ...prev, ner: selections.ner }));
            }
            if (selections?.relations) {
                setParserPrefill((prev) => ({ ...prev, relations: selections.relations }));
            }
            if (selections?.summarization) {
                setParserPrefill((prev) => ({ ...prev, summarization: selections.summarization }));
            }
        } else {
            updateUserCode(functionParserExample);
            setParserSelectionsFromCode(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usecase, updateUserCode]);

    const handleParserPrefillChange = useCallback((prefill) => {
        const normalizedPrefill = prefill || { classification: null, ner: null, relations: null };
        setParserPrefill((prev) => {
            const prevKey = JSON.stringify(prev);
            const nextKey = JSON.stringify(normalizedPrefill);
            if (prevKey === nextKey) {
                return prev;
            }
            return normalizedPrefill;
        });
    }, []);

    // Update userCode when parserPrefill changes (auto-generate logic)
    useEffect(() => {
        if (!usecase) return;

        const snippet = generateInitialParserCode(usecase?.task_type, parserPrefill);
        const trimmed = (userCode || '').trim();

        const isDefault = normalizeCode(userCode) === normalizeCode(functionParserExample);
        const isLastAuto = lastAutoGeneratedSnippet && normalizeCode(userCode) === normalizeCode(lastAutoGeneratedSnippet);

        // Always replace if snippet generated and (user code is empty, default, OR was previously auto-generated)
        // This ensures updates when selection/delimiter changes if the user hasn't manually edited the code.
        const shouldReplace = snippet && (!trimmed || isDefault || isLastAuto);

        if (shouldReplace && normalizeCode(snippet) !== normalizeCode(userCode)) {
            updateUserCode(snippet, { fromAuto: true });
        }
    }, [parserPrefill, usecase, userCode, lastAutoGeneratedSnippet, updateUserCode]);

    // Before opening the Inspect-logic editor: when the saved parser doesn't
    // exist yet and the current code is untouched (empty / default / last
    // auto-generated), replace it with the freshly generated snippet.
    const prefillAutoCodeIfUntouched = useCallback(() => {
        const snippet = generateInitialParserCode(usecase?.task_type, parserPrefill);
        const trimmed = (userCode || '').trim();
        const hasExistingParser = !!usecase?.parser_function;
        const lastAutoTrimmed = lastAutoGeneratedSnippet ? lastAutoGeneratedSnippet.trim() : '';
        const defaultTrimmed = functionParserExample.trim();
        const shouldReplace = snippet && !hasExistingParser && (!trimmed || trimmed === defaultTrimmed || trimmed === lastAutoTrimmed);
        if (snippet && shouldReplace) {
            updateUserCode(snippet, { fromAuto: true });
        }
    }, [usecase, parserPrefill, userCode, lastAutoGeneratedSnippet, updateUserCode]);

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const { selectionStart, selectionEnd } = e.target;

            const newValue = userCode.substring(0, selectionStart) + TAB + userCode.substring(selectionEnd);
            updateUserCode(newValue);

            setTimeout(() => {
                e.target.selectionStart = e.target.selectionEnd = selectionStart + 4;
            }, 0);
        }
    };

    const getFullCode = useCallback(() => {
        const indentedUserCode = (userCode || '').split('\n').map(line => {
            return line.trim() ? TAB + line : line;
        }).join('\n');

        return getFunctionSignature(usecase?.task_type) + indentedUserCode;
    }, [userCode, usecase?.task_type]);

    const fullParserCode = useMemo(() => getFullCode(), [getFullCode]);

    return {
        userCode,
        updateUserCode,
        parserPrefill,
        handleParserPrefillChange,
        parserSelectionsFromCode,
        prefillAutoCodeIfUntouched,
        handleKeyDown,
        getFullCode,
        fullParserCode,
    };
};

export default useParserCodeState;
